/**
 * Deck guardrails — a "Check deck" button in the editor's top bar and, once run,
 * a strip of findings under it: banned "whitelisting" wording, promised results,
 * unfinished template text, statistics without a source, and every figure against
 * the deck's facts (its campaign, or the brand's campaign ledger). Each finding
 * jumps to its page. The server answers `available: false` when the checker is off.
 *
 * One rule in the copy: a deck whose numbers were never verified is never called
 * "clean". `facts.available === false` turns the green tick grey, keeps the toast
 * a warning, and shows a line saying what went unchecked.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi, type GuardrailFinding, type GuardrailReport } from '@/cowork/omniad/api/coworkApi';

const TONE: Record<GuardrailFinding['severity'], { dot: string; text: string; word: string }> = {
  error: { dot: 'bg-[#d92d20]', text: 'text-[#b42318]', word: 'Fix' },
  warning: { dot: 'bg-[#f79009]', text: 'text-[#b54708]', word: 'Check' },
  info: { dot: 'bg-[#667085]', text: 'text-[#475467]', word: 'Consider' },
};

/** What the figures were read against, in the words a reader understands. */
export const factsLabel = (source?: 'campaign' | 'brand') => (source === 'brand' ? 'this brand’s campaigns in the datalake' : 'the campaign data');

/** One finding's identity, so a person can mark it reviewed. */
export const findingKey = (f: GuardrailFinding) => `${f.page}|${f.check}|${f.excerpt}`;

export function DeckGuardrails({ presentationId, slidesVersion, onGoToPage, onBeforeCheck, buttonClassName, autoRunKey, onReport, reviewed, onReview }: {
  presentationId: string;
  /** Any change here marks the last report as stale. */
  slidesVersion: unknown;
  onGoToPage: (page: number) => void;
  /** Save pending edits first so the server checks what the user sees. */
  onBeforeCheck?: () => Promise<void>;
  buttonClassName: string;
  /** Changes after Lykke rewrites pages: the check runs by itself and speaks up only when something needs a look. */
  autoRunKey?: number;
  /** The latest report and whether a check is running (the editor turns it into 'needs review' / 'ready to share'). */
  onReport?: (report: GuardrailReport | null, running: boolean, stale: boolean) => void;
  /** Findings the person already looked at ("Looks right"): not shown again. */
  reviewed?: Set<string>;
  /** Step through the findings one by one on their pages. */
  onReview?: () => void;
}) {
  const [raw, setReport] = useState<GuardrailReport | null>(null);
  const report = raw && reviewed?.size ? { ...raw, findings: raw.findings.filter((f) => !reviewed.has(findingKey(f))) } : raw;
  const [running, setRunning] = useState(false);
  const [stale, setStale] = useState(false);
  const [open, setOpen] = useState(true);
  // One line by default (the pages to look at); the full list only when asked for.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (report) setStale(true); }, [slidesVersion]);

  useEffect(() => { onReport?.(raw, running, stale); }, [raw, running, stale]);
  const run = async (quiet = false) => {
    setRunning(true);
    try {
      await onBeforeCheck?.();
      const r = await coworkApi.presentations.guardrails(presentationId);
      setReport(r); setStale(false); setOpen(true); setExpanded(false);
      if (quiet) { /* automatic run: the badge and the strip speak; clean stays silent */ }
      else if (!r.available) toast.message('Deck check is not enabled on this server yet.');
      else if (!r.findings.length) {
        // "Clean" must mean the numbers were read too. When they were not, the toast says what is unchecked.
        if (r.facts?.available) toast.success(`All ${r.pages} pages look clean · ${r.facts.verified} of ${r.facts.figures} figures verified against ${factsLabel(r.facts.source)}${r.facts.charts ? ` (chart values included, ${r.facts.charts} chart${r.facts.charts === 1 ? '' : 's'})` : ''}`);
        else if (r.facts) toast.warning(`Wording looks clean on ${r.pages} pages, but no figure was checked against the datalake. ${r.facts.reason || ''}`.trim());
        else toast.warning(`Wording looks clean on ${r.pages} pages: this server does not check figures yet.`);
      }
    } catch (e) { if (!quiet) toast.error((e as Error).message); }
    finally { setRunning(false); }
  };
  useEffect(() => { if (autoRunKey) void run(true); }, [autoRunKey]);

  const factsNote = !report?.facts ? null
    : report.facts.available
      ? `${report.facts.verified} of ${report.facts.figures} figures verified against ${factsLabel(report.facts.source)}${report.facts.charts ? ` (incl. ${report.facts.charts} chart${report.facts.charts === 1 ? '' : 's'})` : ''}${report.facts.unverified ? `, ${report.facts.unverified} unverified` : ''}${report.facts.unjudged ? ` (${report.facts.unjudged} beyond the per-page limit were not judged)` : ''}`
      : `figures not checked${report.facts.reason ? `: ${report.facts.reason.replace(/\.$/, '')}` : ''}`;
  const factsUnchecked = !!report?.available && !report.facts?.available;

  // Nothing to flag, but the numbers were never read: a quiet line beats a green tick.
  const noticeOnly = report?.available && open && report.findings.length === 0 && factsUnchecked && (
    <div className="border-b border-[#eaecf0] bg-[#f9fafb] px-4 py-2 text-[12px]">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-[#98a2b3]" />
        <span className="font-semibold text-[#475467]">Wording looks clean on {report.pages} pages: no figure was checked against the datalake</span>
        <span className="text-[#98a2b3]">{report.facts?.reason ? `· ${report.facts.reason}` : report.facts ? '· this deck has no campaign or brand data to check against' : '· this server does not check figures yet'}</span>
        <button onClick={() => setOpen(false)} className="ml-auto rounded p-0.5 text-[#98a2b3] hover:bg-[#f2f4f7]" aria-label="Hide deck check"><X className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );

  const flaggedPages = report ? [...new Set(report.findings.map((f) => f.page))].sort((a, b) => a - b) : [];
  const worst = (page: number) => (report?.findings.some((f) => f.page === page && f.severity === 'error') ? 'error' : 'warning');
  const strip = report?.available && open && report.findings.length > 0 && (
    <div className="border-b border-[#fde7c7] bg-[#fffaeb] px-4 py-1.5 text-[12px]">
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[#b54708]" />
        <span className="shrink-0 font-semibold text-[#93370d]">
          {report.findings.length} to check{stale ? ' · deck changed since' : ''}
        </span>
        <span className="flex min-w-0 items-center gap-1 overflow-hidden">
          {flaggedPages.map((pg) => (
            <button key={pg} onClick={() => onGoToPage(pg)} className={`shrink-0 rounded-full border border-[#fedf89] bg-white px-2 py-0 font-medium hover:border-[#f79009] ${TONE[worst(pg)].text}`} title={report.findings.filter((f) => f.page === pg).map((f) => `${f.label}: ${f.excerpt}`).join('\n')}>p{pg}</button>
          ))}
        </span>
        {onReview && <button onClick={onReview} className="shrink-0 rounded-full bg-[#be43ff] px-2.5 py-0.5 font-medium text-white hover:bg-[#a52fe6]">Review</button>}
        <button onClick={() => setExpanded((v) => !v)} className="shrink-0 font-medium text-[#93370d] underline-offset-2 hover:underline">{expanded ? 'Hide details' : 'Details'}</button>
        <button onClick={() => setOpen(false)} className="ml-auto shrink-0 rounded p-0.5 text-[#98a2b3] hover:bg-[#fef0c7]" aria-label="Hide deck check"><X className="h-3.5 w-3.5" /></button>
      </div>
      {expanded ? (
        <div className="mt-1.5 max-h-40 overflow-y-auto">
          <div className={factsUnchecked ? 'mb-1 font-semibold text-[#b54708]' : 'mb-1 text-[#98a2b3]'}>{report.pages} pages checked{report.unchecked ? `, ${report.unchecked} skipped` : ''}{factsNote ? ` · ${factsNote}` : ''}</div>
          <ul className="flex flex-col gap-0.5">
            {report.findings.map((f, i) => (
              <li key={i} className="flex min-w-0 items-center gap-1.5">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE[f.severity].dot}`} />
                <button onClick={() => onGoToPage(f.page)} className={`shrink-0 font-semibold underline-offset-2 hover:underline ${TONE[f.severity].text}`} title={f.hint}>Page {f.page}: {f.label}</button>
                <span className="truncate text-[#667085]" title={f.excerpt}>{f.excerpt}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );

  // The strip lives just under the top bar: portal it into the editor's own slot so the toolbar stays one row.
  const slot = typeof document !== 'undefined' ? document.getElementById('deck-guardrails-slot') : null;
  // The green tick means the whole deck passed — wording AND figures. Unverified numbers keep it grey.
  const clean = report?.available && !report.findings.length && !stale && report.facts?.available !== false;
  return (
    <>
      <button onClick={() => void run()} disabled={running} className={buttonClassName} title="Check every page for banned wording, promised results, unfinished text, unsourced statistics, and every figure against the datalake">
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className={`h-3.5 w-3.5 ${clean ? 'text-[#067647]' : ''}`} />}
        {report?.available && report.findings.length ? ` ${report.findings.length} to check` : clean ? <span className="text-[#067647]"> Facts checked</span> : ' Check facts'}
      </button>
      {slot && (strip || noticeOnly) ? createPortal(strip || noticeOnly, slot) : null}
    </>
  );
}
